import axios from "axios"
import { useAppDispatch, useAppSelector } from "../../store"
import { IsLoading, Session, setLoading, signin, signout } from "./slice"
import { useRouter } from "next/navigation"
import { useCallback, useEffect, useMemo } from "react"
import { Admin, Operator } from "@/savaradio/common"
import { useStoreRealtime } from "../realtime/hook"

export type Credential = { username: string, password: string }
export type SignupCredential = { role: "operator", otp: string, otp_hashed: string } & Omit<Operator, "_id" | "validated" | "avatar" | "token">

export function useStoreSession(redirect?: string, init?: boolean) {
  const dispatch = useAppDispatch()
  const router = useRouter()
  
  const operators = useAppSelector(state => state.operator_slice.value)
  const admins = useAppSelector(state => state.admin_slice.value)
  const isLoading = useAppSelector(state => state.session_slice.isLoading)
  const session = useAppSelector(state => state.session_slice.value)
  
  const { signout_all } = useStoreRealtime()

  const setIsLoading = useCallback((p: Partial<IsLoading>) => {
    dispatch(setLoading(p))
  }, [dispatch])

  const self = useMemo<Admin | Operator | undefined>(() => {
    if (!session) return undefined
    if (session.role === "admin") {
      return admins?.find(admin => admin._id === session.id)
    }
    if (session.role === "operator") {
      return operators?.find(operator => operator._id === session.id)
    }
    return undefined
  }, [admins, operators, session])

  const register_token = useCallback((session: Session) => {
    sessionStorage.setItem("session", JSON.stringify(session))
    dispatch(signin(session))
  }, [dispatch])

  const retrieve_session = useCallback(async () => {
    if (isLoading.retrieve) return
    
    setIsLoading({ retrieve: true })
    const stored = sessionStorage.getItem("session")
    try {
      const { token, id } = JSON.parse(stored || "{}")
      if (token && id) {
        const { data } = await axios.post("/api/auth/tokenize", { token, id }, { responseType: "json" })
        if (data.role && ["admin", "operator"].includes(data.role)) {
          register_token({ token, id, role: data.role })
          return data.role
        }
      }
      return null
    } catch {
      return null
    } finally {
      setIsLoading({ retrieve: false })
    }
  }, [isLoading.retrieve, setIsLoading, register_token])

  const sign_in = useCallback(async (credential: Credential) => {
    setIsLoading({ signin: true })
    try {
      const { data } = await axios.post("/api/auth/credential", credential, { responseType: "json" })
      if (data && data.id && data.token) {
        register_token(data)
        if (redirect) router.push(redirect)
        return data
      }
      return null
    } catch {
      return null
    } finally {
      setIsLoading({ signin: false })
    }
  }, [setIsLoading, register_token, redirect, router])

  const sign_out = useCallback(() => {
    setIsLoading({ signout: true })
    sessionStorage.removeItem("session")
    if (session) signout_all(session)
    dispatch(signout())
    setIsLoading({ signout: false })
    router.push("/auth/signin")
  }, [dispatch, session, signout_all, router, setIsLoading])

  const reset_pass = useCallback(async (id: string, role: "admin" | "operator", password: string, token: string) => {
    setIsLoading({ reset_pass: true })
    try {
      const { data } = await axios.post("/api/auth/resetpass", { id, token, password, role }, { responseType: "json" })
      if (data.id && data.token) register_token(data)
      return data
    } catch {
      return null
    } finally {
      setIsLoading({ reset_pass: false })
    }
  }, [setIsLoading, register_token])

  const check_link = useCallback(async (token: string) => {
    setIsLoading({ check_link: true })
    try {
      const { data } = await axios.post("/api/auth/verifylink", { token }, { responseType: "json" })
      return data
    } catch {
      return null
    } finally {
      setIsLoading({ check_link: false })
    }
  }, [setIsLoading])

  // Initialisation au montage
  useEffect(() => {
    if (init && !session && !isLoading.retrieve) {
      retrieve_session()
    }
  }, [init, session, retrieve_session, isLoading.retrieve])

  const initializing = useMemo(() => {
    return isLoading.retrieve && !session
  }, [isLoading.retrieve, session])

  return useMemo(() => ({
    self,
    session,
    isLoading,
    initializing,
    retrieve: retrieve_session,
    signin: sign_in,
    signout: sign_out,
    reset_pass,
    check_link,
    register_token
  }), [self, session, isLoading, initializing, retrieve_session, sign_in, sign_out, reset_pass, check_link, register_token])
}